/*
 * decaffeinate suggestions:
 * DS101: Remove unnecessary use of Array.from
 * DS102: Remove unnecessary code created because of implicit returns
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */

import React from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import {Sequence} from 'components/Sequence';
import {CRISPRAnalysis, CRISPRAnalysisSequenceAttributes, CRISPRRegion} from 'constants/CRISPRConstants';
import {SequenceVersion} from 'constants/SequenceConstants';
import SearchDNAData from 'molbio/biological-search/SearchDNAData';
import CRISPRAnalysisActions from 'molbio/crispr/CRISPRAnalysisActions';
import * as CRISPRUtil from 'molbio/crispr/CRISPRUtil';
import * as sequtil from 'molbio/SeqUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

interface RegenerateAnalysisPreviewProps {
  regions: Array<CRISPRRegion>;
  regeneratingAnalysis: boolean;
  sequence: CRISPRAnalysisSequenceAttributes;
}

class RegenerateAnalysisPreview extends React.Component<RegenerateAnalysisPreviewProps> {
  displayRegion = (region: CRISPRRegion): string => `(${sequtil.modelStartToDisplay(
    region.start,
    this.props.sequence.startIndex
  )}, \
${
  region.end === 0
    ? 'end'
    : sequtil.modelEndToDisplay(region.end, this.props.sequence.length, this.props.sequence.startIndex)
})`;

  formatRegions = (regions: Array<CRISPRRegion>, verb: string, className: string): JSX.Element | null => {
    if (regions.length === 0) {
      return null;
    }

    const displays = Array.from(regions)
      .map((region) => this.displayRegion(region))
      .join(', ');
    return (
      <p className={className} key={verb}>
        {`${benchlingUtil.pluralize(regions.length, 'Target region')} ${displays} will be ${verb}.`}
      </p>
    );
  };

  override shouldComponentUpdate(nextProps: RegenerateAnalysisPreviewProps, nextState: {}): boolean {
    return !nextProps.regeneratingAnalysis;
  }

  override render(): JSX.Element {
    const willBeDeleted = [];
    const willBeKept = [];
    for (const region of Array.from(this.props.regions)) {
      if (CRISPRUtil.canRegenerateRegion(region, this.props.sequence.length)) {
        willBeKept.push(region);
      } else {
        willBeDeleted.push(region);
      }
    }

    return (
      <div>
        {this.props.regions.length === 0 ? (
          <p className="u-primary-color">No target regions found in analysis. No changes will be made.</p>
        ) : (
          [
            this.formatRegions(willBeDeleted, 'deleted', 'u-danger-color'),
            this.formatRegions(willBeKept, 'kept', 'u-success-color'),
          ]
        )}
      </div>
    );
  }
}

interface CRISPRAnalysisTabBlockedProps {
  allowEdits: boolean;
  analysis: CRISPRAnalysis;
  findGuidesParams: {
    search: SearchDNAData;
    sequence: Sequence;
  };
  openSequenceVersion: (versionId: number) => void;
  originalSequenceVersion?: SequenceVersion;
  regeneratingAnalysis: boolean;
  sequence: CRISPRAnalysisSequenceAttributes;
  versionIds: Array<number>;
}

interface CRISPRAnalysisTabBlockedState {
  showRegenerateAnalysisPreview: boolean;
}

class CRISPRAnalysisTabBlocked extends React.Component<
  CRISPRAnalysisTabBlockedProps,
  CRISPRAnalysisTabBlockedState
> {
  override state: CRISPRAnalysisTabBlockedState = {
    showRegenerateAnalysisPreview: false,
  };

  regenerateAnalysis = (): void => {
    CRISPRAnalysisActions.regenerateAnalysis(
      this.props.analysis,
      this.props.sequence,
      this.props.findGuidesParams
    );
  };

  override componentDidMount(): void {
    CRISPRAnalysisActions.fetchOriginalSequenceVersion(this.props.analysis.id);
  }

  override UNSAFE_componentWillReceiveProps(nextProps: CRISPRAnalysisTabBlockedProps): void {
    if (
      this.props.originalSequenceVersion != null &&
      !Array.from(nextProps.versionIds).includes(this.props.originalSequenceVersion.id)
    ) {
      CRISPRAnalysisActions.fetchOriginalSequenceVersion(this.props.analysis.id);
    }
  }

  override render(): JSX.Element {
    return (
      <div className="alert alert-warning">
        <p>The sequence associated with this analysis has changed.</p>
        {this.props.originalSequenceVersion != null ? (
          <p>
            {'Please revert sequence to '}
            <a
              className="alert-link"
              // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
              onClick={() => this.props.openSequenceVersion(this.props.originalSequenceVersion!.id)}
            >
              {this.props.originalSequenceVersion.name}
            </a>
            {' to view this analysis, or regenerate analysis with current sequence.'}
          </p>
        ) : (
          <p>Regenerate analysis with current sequence to continue.</p>
        )}
        {this.state.showRegenerateAnalysisPreview ? (
          <div className="u-tiny-spacing-top">
            <RegenerateAnalysisPreview
              regions={this.props.analysis.regions}
              regeneratingAnalysis={this.props.regeneratingAnalysis}
              sequence={this.props.sequence}
            />
          </div>
        ) : undefined}
        <p>
          {this.state.showRegenerateAnalysisPreview ? (
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.SMALL}
              onClick={this.regenerateAnalysis}
              disabled={this.props.regeneratingAnalysis || !this.props.allowEdits}
              text={this.props.regeneratingAnalysis ? 'Regenerating analysis...' : 'Continue'}
            />
          ) : (
            <Button
              buttonStyle={ButtonStyle.PRIMARY}
              size={ButtonSize.SMALL}
              onClick={() => {
                this.setState({showRegenerateAnalysisPreview: true});
              }}
              disabled={!this.props.allowEdits}
              text="Regenerate Analysis"
            />
          )}
        </p>
      </div>
    );
  }
}

export default CRISPRAnalysisTabBlocked;
